import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { auth, useAuth } from "./db.ts";
import { Button } from "./uiComponents.tsx";
import { logError } from "./analytics.ts";
import { monthlyPaymentPerLine } from "../../constants.ts";

const loginEmail = signal("");
const emailSent = signal(false);

const inputStyle = {
  height: 44,
  fontSize: 16,
  padding: "0 12px",
  border: "1.5px solid var(--border)",
  borderRadius: "var(--radius) 0 0 var(--radius)",
  boxSizing: "border-box",
  outline: "none",
  background: "var(--surface-alt)",
  color: "var(--text)",
  marginBottom: 0,
  flex: 1,
} as const;

const buttonStyle = {
  height: 44,
  fontSize: 16,
  borderRadius: "0 var(--radius) var(--radius) 0",
  marginLeft: -1.5, // overlap border
  padding: "0 28px",
  boxSizing: "border-box",
  display: "block",
  border: "1.5px solid var(--border)",
  borderLeft: "none",
  background: "var(--primary)",
  color: "var(--text)",
  fontWeight: 600,
  cursor: "pointer",
} as const;

const Email = () => (
  <div style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
    <input
      style={inputStyle}
      placeholder="Enter email"
      type="email"
      value={loginEmail.value}
      onInput={({ currentTarget: { value } }) => {
        loginEmail.value = value;
      }}
    />
    <Button
      style={buttonStyle}
      onClick={() => {
        if (!loginEmail.value) {
          toast("Fill in your email!");
          return;
        }
        auth
          .sendMagicCode({ email: loginEmail.value })
          .then(() => {
            emailSent.value = true;
          })
          .catch((err) => {
            toast(err.body?.message);
            logError(err);
            loginEmail.value = "";
          });
      }}
    >
      Sign up
    </Button>
  </div>
);

const MagicCode = () => {
  const [code, setCode] = useState("");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
      }}
    >
      <div
        style={{
          textAlign: "center",
          fontSize: "1.05em",
          color: "var(--text)",
        }}
      >
        Okay, we sent you an email!<br />What was the code?
      </div>
      <div style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
        <input
          type="number"
          placeholder="123456..."
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
          style={inputStyle}
        />
        <Button
          onClick={() => {
            auth
              .signInWithMagicCode({ email: loginEmail.value, code })
              .then(() => {
                emailSent.value = false;
                toast("Signed in!");
              })
              .catch((err) => {
                toast(`Wrong code 😢`);
                logError(err);
                setCode("");
              });
          }}
          style={buttonStyle}
        >
          Verify
        </Button>
      </div>
      <div
        style={{ fontSize: "0.8em", color: "var(--text-muted)", opacity: 0.8 }}
      >
        if you don't see it, check your spam folder
      </div>
    </div>
  );
};

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  padding: "60px 20px",
  gap: 56,
  boxSizing: "border-box",
} as const;

const logoContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  textAlign: "center",
  maxWidth: 650,
} as const;

const titleStyle = {
  margin: 0,
  fontSize: "3.2em",
  color: "var(--text)",
  fontWeight: 800,
  letterSpacing: "-0.03em",
} as const;

const subtitleStyle = {
  color: "var(--text-muted)",
  fontSize: "1.2em",
  lineHeight: 1.5,
  margin: 0,
  maxWidth: 550,
} as const;

const italicSubtitleStyle = {
  color: "var(--text-muted)",
  fontSize: "0.95em",
  margin: "4px 0 0",
  maxWidth: 550,
  fontStyle: "italic",
  opacity: 0.8,
} as const;

const pricingBadgeStyle = {
  color: "var(--primary)",
  fontSize: "1em",
  margin: "12px 0 0",
  fontWeight: 600,
  background: "rgba(59, 130, 246, 0.1)",
  padding: "6px 18px",
  borderRadius: "100px",
  border: "1px solid rgba(59, 130, 246, 0.2)",
} as const;

const loginCardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  padding: 32,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  width: "100%",
  maxWidth: 450,
  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  textAlign: "center",
} as const;

const sectionTitleStyle = {
  fontSize: "1.85em",
  fontWeight: 800,
  margin: "24px 0 6px",
  color: "var(--text)",
  textAlign: "center",
  letterSpacing: "-0.01em",
} as const;

const sectionSubtitleStyle = {
  color: "var(--text-muted)",
  fontSize: "1.05em",
  margin: "0 0 32px",
  textAlign: "center",
  maxWidth: 550,
} as const;

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 24,
  width: "100%",
} as const;

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: 24,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  textAlign: "left",
} as const;

const cardIconContainerStyle = (color: string) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 48,
  height: 48,
  borderRadius: "var(--radius)",
  background: `rgba(${color}, 0.1)`,
  color: `rgb(${color})`,
} as const);

const cardTitleStyle = {
  fontSize: "1.25em",
  fontWeight: 700,
  margin: 0,
  color: "var(--text)",
} as const;

const cardDescStyle = {
  fontSize: "0.95em",
  color: "var(--text-muted)",
  lineHeight: 1.5,
  margin: 0,
} as const;

const listStyle = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  fontSize: "0.9em",
} as const;

const listItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "var(--text-muted)",
} as const;

const bulletStyle = (color: string) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: `rgb(${color})`,
  flexShrink: 0,
} as const);

export const AuthScreen = () => {
  const { user } = useAuth();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        minHeight: "100vh",
        background: "var(--surface)",
        color: "var(--text)",
        overflowY: "auto",
      }}
    >
      <div style={containerStyle}>
        {/* Header / Brand */}
        <div style={logoContainerStyle}>
          <h1 style={titleStyle}>🚀 Supergreen</h1>
          <p style={subtitleStyle}>
            Unified API and managed infrastructure for messaging, automated
            scraping, and virtual communications.
          </p>
          <p style={italicSubtitleStyle}>
            Connect accounts, route messages instantly, and build reliable
            integrations on dedicated isolated instances.
          </p>
          <div style={pricingBadgeStyle}>
            ${monthlyPaymentPerLine}/month per service slot — free trial
            available
          </div>
        </div>

        {/* Login Card */}
        <div style={loginCardStyle}>
          <h2
            style={{ margin: "0 0 8px 0", fontSize: "1.35em", fontWeight: 700 }}
          >
            {user ? "Welcome Back" : "Access your API Console"}
          </h2>
          <p
            style={{
              margin: "0 0 16px 0",
              fontSize: "0.9em",
              color: "var(--text-muted)",
            }}
          >
            {user
              ? "You are logged in. Continue to your account dashboard."
              : "Sign in or create a new developer account instantly."}
          </p>
          {user
            ? (
              <a
                href="/app"
                style={{
                  ...buttonStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "var(--radius)",
                  marginLeft: 0,
                  borderLeft: "1.5px solid var(--border)",
                  background: "var(--primary)",
                  color: "var(--text)",
                  border: "1.5px solid var(--border)",
                  textDecoration: "none",
                  width: "100%",
                  height: 44,
                }}
              >
                Go to Dashboard
              </a>
            )
            : (
              emailSent.value ? <MagicCode /> : <Email />
            )}
          {!user && (
            <a
              href="/pricing"
              style={{
                color: "var(--primary)",
                textDecoration: "underline",
                fontSize: 14,
                marginTop: 8,
              }}
            >
              View Pricing Details
            </a>
          )}
        </div>

        {/* Services Showcase */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <h2 style={sectionTitleStyle}>
            Unified Communications Infrastructure
          </h2>
          <p style={sectionSubtitleStyle}>
            Everything you need to automate conversations, track notifications,
            and collect platform data with ease.
          </p>

          <div style={gridStyle}>
            {/* WhatsApp */}
            <div style={cardStyle}>
              <div style={cardIconContainerStyle("37, 211, 102")}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </div>
              <h3 style={cardTitleStyle}>WhatsApp API</h3>
              <p style={cardDescStyle}>
                Deploy fully isolated, headless WhatsApp Web instances on
                dedicated, secure virtual machines.
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <div style={bulletStyle("37, 211, 102")} />
                  <span>Dedicated Docker worker isolation</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("37, 211, 102")} />
                  <span>Interactive QR & pairing code auth</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("37, 211, 102")} />
                  <span>Customize profile name, bio, and avatar</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("37, 211, 102")} />
                  <span>Real-time webhook routing</span>
                </li>
              </ul>
            </div>

            {/* Telegram */}
            <div style={cardStyle}>
              <div style={cardIconContainerStyle("0, 136, 204")}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </div>
              <h3 style={cardTitleStyle}>Telegram Client</h3>
              <p style={cardDescStyle}>
                Lightweight, high-performance MTProto daemon for multi-session
                Telegram automation.
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <div style={bulletStyle("0, 136, 204")} />
                  <span>Direct MTProto protocol execution</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("0, 136, 204")} />
                  <span>Interactive login via Telegram App</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("0, 136, 204")} />
                  <span>Programmatic channel & group management</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("0, 136, 204")} />
                  <span>Zero-browser, ultra-light resource footprint</span>
                </li>
              </ul>
            </div>

            {/* Facebook Scraper */}
            <div style={cardStyle}>
              <div style={cardIconContainerStyle("24, 119, 242")}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 style={cardTitleStyle}>Facebook Scraper</h3>
              <p style={cardDescStyle}>
                Automated persistent scraping targeting social group activities
                and posts.
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <div style={bulletStyle("24, 119, 242")} />
                  <span>Playwright remote browser automation</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("24, 119, 242")} />
                  <span>Execution powered by Kernel APIs</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("24, 119, 242")} />
                  <span>Persistent authorization & session state</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("24, 119, 242")} />
                  <span>Forward posts instantly to webhooks</span>
                </li>
              </ul>
            </div>

            {/* SMS Numbers */}
            <div style={cardStyle}>
              <div style={cardIconContainerStyle("168, 85, 247")}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
              </div>
              <h3 style={cardTitleStyle}>Virtual SMS Numbers</h3>
              <p style={cardDescStyle}>
                Instantly buy local virtual phone numbers (DIDs) with direct
                routing.
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <div style={bulletStyle("168, 85, 247")} />
                  <span>Integrated virtual carrier provisioning</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("168, 85, 247")} />
                  <span>Flat-rate pricing per active number</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("168, 85, 247")} />
                  <span>Forward incoming SMS to your webhooks</span>
                </li>
                <li style={listItemStyle}>
                  <div style={bulletStyle("168, 85, 247")} />
                  <span>Persistent database SMS logs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div
          style={{
            textAlign: "center",
            width: "100%",
            borderTop: "1px solid var(--border)",
            paddingTop: 20,
          }}
        >
          <a
            href="/terms"
            style={{
              color: "var(--primary)",
              textDecoration: "underline",
              fontSize: 13,
              opacity: 0.8,
            }}
          >
            Terms & Privacy
          </a>
        </div>
      </div>
    </div>
  );
};

export { AuthScreen as Login };
